(() => {
  'use strict';

  if (window.__SAF_WISHLIST_UNIFIED__) return;
  window.__SAF_WISHLIST_UNIFIED__ = true;

  const CONFIG = {
    localStorageKey: 'saf_wishlist_items',
    stateUrl: '/apps/control-panel/wishlist',
    productsUrl: '/apps/control-panel/wishlist/products',
    removeUrl: '/apps/control-panel/wishlist/remove',
  };

  const listeners = new Set();

  let stateCache = null;
  let productsCache = null;
  let statePromise = null;
  let productsPromise = null;

  const uniqueProductIds = (items) =>
    [...new Set(
      (Array.isArray(items) ? items : []).filter(
        (item) =>
          typeof item === 'string' &&
          item.startsWith('gid://shopify/Product/')
      )
    )].slice(0, 50);

  const getLocalItems = () => {
    try {
      return uniqueProductIds(
        JSON.parse(
          localStorage.getItem(CONFIG.localStorageKey) || '[]'
        )
      );
    } catch (_) {
      return [];
    }
  };

  const setLocalItems = (items) => {
    try {
      localStorage.setItem(
        CONFIG.localStorageKey,
        JSON.stringify(uniqueProductIds(items))
      );
    } catch (_) {}
  };

  const parseVariantNumericId = (gid) => {
    if (typeof gid !== 'string') return '';

    const match = gid.match(
      /ProductVariant\/(\d+)$/
    );

    return match ? match[1] : '';
  };

  const getLocale = (element) =>
    element?.dataset?.locale ||
    document.documentElement.lang ||
    'en';

  const formatMoney = (
    money,
    locale
  ) => {
    if (!money) return '';

    const amount = Number(
      money.amount
    );

    const currency =
      money.currency_code ||
      money.currencyCode ||
      'EUR';

    if (
      !Number.isFinite(amount)
    ) {
      return '';
    }

    try {
      return new Intl.NumberFormat(
        locale || undefined,
        {
          style: 'currency',
          currency,
        }
      ).format(amount);
    } catch (_) {
      return `${amount.toFixed(2)} ${currency}`;
    }
  };

  const moneyNumber = (money) => {
    if (!money) {
      return Number.POSITIVE_INFINITY;
    }

    const value = Number(
      money.amount
    );

    return Number.isFinite(value)
      ? value
      : Number.POSITIVE_INFINITY;
  };

  const getComparePrice = (
    product
  ) =>
    product?.compare_at_price ||
    product?.compareAtPrice ||
    product?.variant?.compare_at_price ||
    product?.variant?.compareAtPrice ||
    null;

  const getProductUrl = (
    product
  ) =>
    typeof product?.url === 'string' &&
    product.url.startsWith(
      '/products/'
    )
      ? product.url
      : '#';

  const invalidate = () => {
    stateCache = null;
    productsCache = null;
    statePromise = null;
    productsPromise = null;
  };

  const emit = (
    detail = {}
  ) => {
    listeners.forEach(
      (listener) => {
        try {
          listener(detail);
        } catch (error) {
          console.error(
            'SAF wishlist listener error:',
            error
          );
        }
      }
    );
  };

  const notifyUpdated = (
    detail = {}
  ) => {
    invalidate();
    emit(detail);

    window.dispatchEvent(
      new CustomEvent(
        'saf:wishlist-updated',
        {
          detail,
        }
      )
    );
  };

  const getAccountState = async (
    force = false
  ) => {
    if (
      !force &&
      stateCache
    ) {
      return stateCache;
    }

    if (
      !force &&
      statePromise
    ) {
      return statePromise;
    }

    statePromise = fetch(
      CONFIG.stateUrl,
      {
        method: 'GET',
        credentials:
          'same-origin',
        headers: {
          Accept:
            'application/json',
        },
      }
    )
      .then(
        (response) => {
          if (
            !response.ok
          ) {
            throw new Error(
              `Wishlist state failed: ${response.status}`
            );
          }

          return response.json();
        }
      )
      .then((data) => {
        stateCache =
          data || {};

        return stateCache;
      })
      .finally(() => {
        statePromise = null;
      });

    return statePromise;
  };

  const getCount = async (
    force = false
  ) => {
    try {
      const state =
        await getAccountState(
          force
        );

      const authenticated =
        state?.authenticated ===
        true;

      if (!authenticated) {
        return {
          authenticated:
            false,
          count:
            getLocalItems()
              .length,
        };
      }

      const count =
        typeof state.count ===
        'number'
          ? state.count
          : Array.isArray(
                state.items
              )
            ? state.items
                .length
            : 0;

      return {
        authenticated:
          true,
        count,
      };
    } catch (error) {
      console.error(
        'SAF wishlist count error:',
        error
      );

      return {
        authenticated:
          false,
        count:
          getLocalItems()
            .length,
      };
    }
  };

  const getProducts = async (
    force = false
  ) => {
    if (
      !force &&
      productsCache
    ) {
      return productsCache;
    }

    if (
      !force &&
      productsPromise
    ) {
      return productsPromise;
    }

    productsPromise =
      (async () => {
        const state =
          await getAccountState(
            force
          );

        const authenticated =
          state?.authenticated ===
          true;

        let url =
          CONFIG.productsUrl;

        if (!authenticated) {
          const ids =
            getLocalItems();

          if (!ids.length) {
            productsCache = {
              authenticated:
                false,
              products: [],
            };

            return productsCache;
          }

          url += `?ids=${encodeURIComponent(
            ids.join(',')
          )}`;
        }

        const response =
          await fetch(
            url,
            {
              method:
                'GET',
              credentials:
                'same-origin',
              headers: {
                Accept:
                  'application/json',
              },
            }
          );

        if (
          !response.ok
        ) {
          throw new Error(
            `Wishlist products failed: ${response.status}`
          );
        }

        const data =
          await response.json();

        productsCache = {
          authenticated,
          products:
            Array.isArray(
              data.products
            )
              ? data.products
              : [],
        };

        return productsCache;
      })().finally(() => {
        productsPromise =
          null;
      });

    return productsPromise;
  };

  const removeProduct =
    async (
      productId
    ) => {
      if (!productId) {
        return;
      }

      const state =
        await getAccountState();

      const authenticated =
        state?.authenticated ===
        true;

      if (authenticated) {
        const response =
          await fetch(
            CONFIG.removeUrl,
            {
              method:
                'POST',
              credentials:
                'same-origin',
              headers: {
                Accept:
                  'application/json',
                'Content-Type':
                  'application/json',
              },
              body:
                JSON.stringify({
                  product_id:
                    productId,
                }),
            }
          );

        if (
          !response.ok
        ) {
          throw new Error(
            `Wishlist remove failed: ${response.status}`
          );
        }
      } else {
        setLocalItems(
          getLocalItems().filter(
            (id) =>
              id !==
              productId
          )
        );
      }

      notifyUpdated({
        action:
          'remove',
        productId,
      });
    };

  const addToCart =
    async (
      product
    ) => {
      const variant =
        product?.variant ||
        {};

      if (
        !variant.id ||
        variant.available !==
          true
      ) {
        throw new Error(
          'Product variant is not available'
        );
      }

      if (
        window.Shopify
          ?.actions
          ?.updateCart
      ) {
        const result =
          await window.Shopify.actions.updateCart(
            {
              lines: [
                {
                  merchandiseId:
                    variant.id,
                  quantity: 1,
                },
              ],
            }
          );

        if (
          result
            ?.userErrors
            ?.length
        ) {
          throw new Error(
            result
              .userErrors[0]
              .message
          );
        }

        document.dispatchEvent(
          new CustomEvent(
            'saf:cart-added',
            {
              detail: {
                product,
                cart:
                  result.cart,
              },
            }
          )
        );

        return result;
      }

      const numericId =
        parseVariantNumericId(
          variant.id
        );

      if (!numericId) {
        throw new Error(
          'Missing variant id'
        );
      }

      const response =
        await fetch(
          '/cart/add.js',
          {
            method:
              'POST',
            credentials:
              'same-origin',
            headers: {
              Accept:
                'application/json',
              'Content-Type':
                'application/json',
            },
            body:
              JSON.stringify({
                items: [
                  {
                    id:
                      Number(
                        numericId
                      ),
                    quantity:
                      1,
                  },
                ],
              }),
          }
        );

      if (
        !response.ok
      ) {
        throw new Error(
          `Cart add failed: ${response.status}`
        );
      }

      const result =
        await response.json();

      document.dispatchEvent(
        new CustomEvent(
          'saf:cart-added',
          {
            detail: {
              product,
              cart:
                result,
            },
          }
        )
      );

      return result;
    };

  const core = {
    getLocalItems,
    setLocalItems,
    getAccountState,
    getCount,
    getProducts,
    removeProduct,
    addToCart,
    formatMoney,
    moneyNumber,
    getComparePrice,
    getProductUrl,
    invalidate,

    onUpdate(
      callback
    ) {
      listeners.add(
        callback
      );

      return () => {
        listeners.delete(
          callback
        );
      };
    },
  };

  window.SAFWishlist =
    core;


  /* =========================================================
     DRAWER
     ========================================================= */

  const initDrawer = () => {
    const widget =
      document.querySelector(
        '[data-saf-wishlist-widget]'
      );

    if (
      !widget ||
      widget.dataset
        .safUnifiedInitialized ===
        'true'
    ) {
      return;
    }

    widget.dataset.safUnifiedInitialized =
      'true';

    const openButton =
      widget.querySelector(
        '[data-saf-wishlist-open]'
      );

    const closeButton =
      widget.querySelector(
        '[data-saf-wishlist-close]'
      );

    const overlay =
      widget.querySelector(
        '[data-saf-wishlist-overlay]'
      );

    const drawer =
      widget.querySelector(
        '[data-saf-wishlist-drawer]'
      );

    const body =
      widget.querySelector(
        '[data-saf-wishlist-body]'
      );

    const headingCount =
      widget.querySelector(
        '[data-saf-wishlist-heading-count]'
      );

    const countBadge =
      widget.querySelector(
        '[data-saf-wishlist-count]'
      );

    const locale =
      getLocale(widget);

    if (
      !drawer ||
      !body ||
      !closeButton
    ) {
      return;
    }

    let currentProducts =
      [];

    let loading =
      false;

    const updateCount = (
      count
    ) => {
      const safeCount =
        Number.isFinite(
          Number(count)
        )
          ? Math.max(
              0,
              Number(count)
            )
          : 0;

      if (
        headingCount
      ) {
        headingCount.textContent =
          `(${safeCount})`;
      }

      if (
        countBadge
      ) {
        countBadge.textContent =
          String(
            safeCount
          );

        countBadge.hidden =
          safeCount === 0;
      }

      document
        .querySelectorAll(
          '[data-saf-wishlist-global-count]'
        )
        .forEach(
          (element) => {
            element.textContent =
              String(
                safeCount
              );

            element.hidden =
              safeCount ===
              0;
          }
        );
    };

    const showMessage = (
      message
    ) => {
      body.replaceChildren();

      const element =
        document.createElement(
          'div'
        );

      element.className =
        'saf-wishlist-widget__message';

      element.textContent =
        message;

      body.appendChild(
        element
      );
    };

    const createDrawerProduct =
      (
        product
      ) => {
        const article =
          document.createElement(
            'article'
          );

        article.className =
          'saf-wishlist-widget__product';

        article.dataset.productId =
          product.id ||
          '';

        const removeButton =
          document.createElement(
            'button'
          );

        removeButton.type =
          'button';

        removeButton.className =
          'saf-wishlist-widget__product-remove';

        removeButton.setAttribute(
          'aria-label',
          `Remove ${
            product.title ||
            'product'
          } from wishlist`
        );

        removeButton.innerHTML =
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

        removeButton.addEventListener(
          'click',
          async () => {
            if (
              removeButton.disabled
            ) {
              return;
            }

            removeButton.disabled =
              true;

            try {
              await core.removeProduct(
                product.id
              );

              currentProducts =
                currentProducts.filter(
                  (
                    item
                  ) =>
                    item.id !==
                    product.id
                );

              renderProducts();
            } catch (
              error
            ) {
              console.error(
                'SAF wishlist drawer remove error:',
                error
              );

              removeButton.disabled =
                false;
            }
          }
        );

        const imageWrap =
          document.createElement(
            'div'
          );

        imageWrap.className =
          'saf-wishlist-widget__image-wrap';

        if (
          product.image
            ?.url
        ) {
          const image =
            document.createElement(
              'img'
            );

          image.className =
            'saf-wishlist-widget__image';

          image.src =
            product.image.url;

          image.alt =
            product.image
              .altText ||
            product.title ||
            '';

          image.loading =
            'lazy';

          image.width =
            220;

          image.height =
            220;

          imageWrap.appendChild(
            image
          );
        } else {
          const placeholder =
            document.createElement(
              'div'
            );

          placeholder.className =
            'saf-wishlist-widget__image-placeholder';

          placeholder.textContent =
            'IMG';

          imageWrap.appendChild(
            placeholder
          );
        }

        const info =
          document.createElement(
            'div'
          );

        info.className =
          'saf-wishlist-widget__product-info';

        const vendor =
          document.createElement(
            'div'
          );

        vendor.className =
          'saf-wishlist-widget__vendor';

        vendor.textContent =
          product.vendor ||
          '';

        const title =
          document.createElement(
            'div'
          );

        title.className =
          'saf-wishlist-widget__title';

        title.textContent =
          product.title ||
          '';

        const price =
          document.createElement(
            'div'
          );

        price.className =
          'saf-wishlist-widget__price';

        price.textContent =
          core.formatMoney(
            product.price,
            locale
          );

        const actions =
          document.createElement(
            'div'
          );

        actions.className =
          'saf-wishlist-widget__actions';

        const addCart =
          document.createElement(
            'button'
          );

        addCart.type =
          'button';

        addCart.className =
          'saf-wishlist-widget__add-cart';

        if (
          !product.variant
            ?.id ||
          product.variant
            .available !==
            true
        ) {
          addCart.textContent =
            'SOLD OUT';

          addCart.disabled =
            true;
        } else {
          addCart.textContent =
            'ADD TO CART';

          addCart.addEventListener(
            'click',
            async () => {
              if (
                addCart.disabled
              ) {
                return;
              }

              const originalText =
                addCart.textContent;

              addCart.disabled =
                true;

              addCart.textContent =
                'ADDING...';

              try {
                await core.addToCart(
                  product
                );

                addCart.textContent =
                  'ADDED';

                setTimeout(
                  () => {
                    addCart.disabled =
                      false;

                    addCart.textContent =
                      originalText;
                  },
                  1400
                );
              } catch (
                error
              ) {
                console.error(
                  'SAF drawer add-to-cart error:',
                  error
                );

                addCart.disabled =
                  false;

                addCart.textContent =
                  'TRY AGAIN';
              }
            }
          );
        }

        const viewProduct =
          document.createElement(
            'a'
          );

        viewProduct.className =
          'saf-wishlist-widget__view';

        viewProduct.textContent =
          'VIEW PRODUCT';

        viewProduct.href =
          core.getProductUrl(
            product
          );

        actions.append(
          addCart,
          viewProduct
        );

        info.append(
          vendor,
          title,
          price,
          actions
        );

        article.append(
          removeButton,
          imageWrap,
          info
        );

        return article;
      };

    const renderProducts =
      () => {
        body.replaceChildren();

        if (
          !currentProducts.length
        ) {
          showMessage(
            'Your wishlist is empty.'
          );

          updateCount(
            0
          );

          return;
        }

        currentProducts.forEach(
          (
            product
          ) => {
            body.appendChild(
              createDrawerProduct(
                product
              )
            );
          }
        );

        updateCount(
          currentProducts.length
        );
      };

    const refreshCount =
      async (
        force = false
      ) => {
        const result =
          await core.getCount(
            force
          );

        updateCount(
          result.count
        );
      };

    const loadProducts =
      async (
        force = false
      ) => {
        if (loading) {
          return;
        }

        loading =
          true;

        showMessage(
          'Loading wishlist...'
        );

        try {
          const result =
            await core.getProducts(
              force
            );

          currentProducts =
            [
              ...result.products,
            ];

          renderProducts();
        } catch (
          error
        ) {
          console.error(
            'SAF wishlist drawer load error:',
            error
          );

          showMessage(
            'Unable to load your wishlist.'
          );
        } finally {
          loading =
            false;
        }
      };

    const openDrawer =
      async () => {
        widget.classList.add(
          'is-open'
        );

        if (
          openButton
        ) {
          openButton.setAttribute(
            'aria-expanded',
            'true'
          );
        }

        drawer.setAttribute(
          'aria-hidden',
          'false'
        );

        closeButton.focus();

        await loadProducts();
      };

    const closeDrawer =
      () => {
        widget.classList.remove(
          'is-open'
        );

        if (
          openButton
        ) {
          openButton.setAttribute(
            'aria-expanded',
            'false'
          );
        }

        drawer.setAttribute(
          'aria-hidden',
          'true'
        );

        const headerTrigger =
          document.querySelector(
            '.saf-wishlist-header-trigger'
          );

        headerTrigger?.focus();
      };

    openButton?.addEventListener(
      'click',
      openDrawer
    );

    closeButton.addEventListener(
      'click',
      closeDrawer
    );

    overlay?.addEventListener(
      'click',
      closeDrawer
    );

    document.addEventListener(
      'keydown',
      (
        event
      ) => {
        if (
          event.key ===
            'Escape' &&
          widget.classList.contains(
            'is-open'
          )
        ) {
          closeDrawer();
        }
      }
    );

    window.addEventListener(
      'saf:wishlist-open',
      openDrawer
    );

    core.onUpdate(
      async () => {
        await refreshCount(
          true
        );

        if (
          widget.classList.contains(
            'is-open'
          )
        ) {
          await loadProducts(
            true
          );
        }
      }
    );

    refreshCount();
  };


  /* =========================================================
     FULL WISHLIST PAGE
     ========================================================= */
      /* =========================================================
     FULL WISHLIST PAGE
     ========================================================= */

  const initPage = (root) => {
    if (
      !root ||
      root.dataset.safUnifiedInitialized === 'true'
    ) {
      return;
    }

    root.dataset.safUnifiedInitialized = 'true';

    const grid = root.querySelector(
      '[data-saf-wishlist-grid]'
    );

    const status = root.querySelector(
      '[data-saf-wishlist-status]'
    );

    const count = root.querySelector(
      '[data-saf-wishlist-count]'
    );

    const sort = root.querySelector(
      '[data-saf-wishlist-sort]'
    );

    const share = root.querySelector(
      '[data-saf-wishlist-share]'
    );

    const locale = getLocale(root);

    const continueUrl =
      root.dataset.continueUrl ||
      '/collections/all';

    const sectionId =
      root.dataset.sectionId || '';

    let originalProducts = [];
    let products = [];
    let loading = false;
    let ignoreNextPageUpdate = false;
    let renderVersion = 0;

    const nativeCardHtmlCache = new Map();

    let sectionRendererPromise = null;


    /* =========================================================
       SECTION RENDERER
       ========================================================= */

    const getSectionRenderer = async () => {
      if (!sectionRendererPromise) {
        sectionRendererPromise = import(
          '@theme/section-renderer'
        ).then((module) => module.sectionRenderer);
      }

      return sectionRendererPromise;
    };


    /* =========================================================
       COUNT
       ========================================================= */

    const updatePageCount = () => {
      if (!count) return;

      count.textContent =
        `${products.length} ${
          products.length === 1
            ? 'item'
            : 'items'
        }`;
    };


    /* =========================================================
       STATUS
       ========================================================= */

    const showStatus = (
      message,
      empty = false
    ) => {
      if (!status) return;

      status.hidden = false;
      status.replaceChildren();

      const copy = document.createElement('div');
      copy.textContent = message;

      status.appendChild(copy);

      if (empty) {
        const actions =
          document.createElement('div');

        actions.className =
          'saf-wishlist-page__empty-actions';

        const link =
          document.createElement('a');

        link.className =
          'saf-wishlist-page__empty-link';

        link.href = continueUrl;
        link.textContent =
          'CONTINUE SHOPPING';

        actions.appendChild(link);
        status.appendChild(actions);
      }
    };


    const hideStatus = () => {
      if (status) {
        status.hidden = true;
      }
    };


    /* =========================================================
       HEART SVG
       ========================================================= */

    const createHeart = () => {
      const svg =
        document.createElementNS(
          'http://www.w3.org/2000/svg',
          'svg'
        );

      svg.setAttribute(
        'viewBox',
        '0 0 24 24'
      );

      svg.setAttribute(
        'aria-hidden',
        'true'
      );

      svg.innerHTML = `
        <path
          d="M12 20.3 4.4 13A4.9 4.9 0 0 1 11.3 6L12 6.7l.7-.7A4.9 4.9 0 0 1 19.6 13L12 20.3Z"
          fill="currentColor"
        />
      `;

      return svg;
    };


    /* =========================================================
       REMOVE FROM WISHLIST
       ========================================================= */

    const createRemoveButton = (product) => {
      const remove =
        document.createElement('button');

      remove.type = 'button';

      remove.className =
        'saf-wishlist-page__remove';

      remove.setAttribute(
        'aria-label',
        `Remove ${
          product.title || 'product'
        } from wishlist`
      );

      remove.appendChild(
        createHeart()
      );

      remove.addEventListener(
        'click',
        async (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (remove.disabled) return;

          remove.disabled = true;

          ignoreNextPageUpdate = true;

          try {
            await core.removeProduct(
              product.id
            );

            originalProducts =
              originalProducts.filter(
                (item) =>
                  item.id !== product.id
              );

            products =
              products.filter(
                (item) =>
                  item.id !== product.id
              );

            nativeCardHtmlCache.delete(
              core.getProductUrl(product)
            );

            await renderPage();
          } catch (error) {
            ignoreNextPageUpdate = false;
            remove.disabled = false;

            console.error(
              'SAF wishlist page remove error:',
              error
            );
          }
        }
      );

      return remove;
    };


    /* =========================================================
       ADD TO CART
       ========================================================= */

    const createAddToCartButton = (
      product
    ) => {
      const addCart =
        document.createElement('button');

      addCart.type = 'button';

      addCart.className =
        'saf-wishlist-page__cta';

      if (
        !product.variant?.id ||
        product.variant.available !== true
      ) {
        addCart.textContent =
          'SOLD OUT';

        addCart.disabled = true;

        return addCart;
      }

      addCart.textContent =
        'ADD TO CART';

      addCart.addEventListener(
        'click',
        async (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (addCart.disabled) return;

          const originalText =
            addCart.textContent;

          addCart.disabled = true;

          addCart.textContent =
            'ADDING...';

          try {
            await core.addToCart(
              product
            );

            addCart.textContent =
              'ADDED';

            window.setTimeout(
              () => {
                addCart.disabled = false;

                addCart.textContent =
                  originalText;
              },
              1400
            );
          } catch (error) {
            console.error(
              'SAF wishlist add-to-cart error:',
              error
            );

            addCart.disabled = false;

            addCart.textContent =
              'TRY AGAIN';

            window.setTimeout(
              () => {
                addCart.textContent =
                  originalText;
              },
              1600
            );
          }
        }
      );

      return addCart;
    };


    /* =========================================================
       GET NATIVE HORIZON CARD
       ========================================================= */

    const getNativeWishlistCardHtml =
      async (product) => {
        const productUrl =
          core.getProductUrl(product);

        if (
          !sectionId ||
          !productUrl ||
          productUrl === '#'
        ) {
          return null;
        }

        if (
          nativeCardHtmlCache.has(
            productUrl
          )
        ) {
          return nativeCardHtmlCache.get(
            productUrl
          );
        }

        try {
          const sectionRenderer =
            await getSectionRenderer();

          if (
            !sectionRenderer ||
            typeof sectionRenderer
              .getSectionHTML !==
              'function'
          ) {
            throw new Error(
              'Section renderer unavailable'
            );
          }

          const url = new URL(
            productUrl,
            window.location.origin
          );

          /*
           * IMPORTANTE:
           *
           * chiediamo LA STESSA SECTION WISHLIST
           * ma sull'URL del prodotto.
           *
           * In quella richiesta Liquid avrà:
           *
           * product
           * closest.product
           * metafields
           * ecc.
           */

          const sectionMarkup =
            await sectionRenderer
              .getSectionHTML(
                sectionId,
                false,
                url
              );

          const sectionDocument =
            new DOMParser()
              .parseFromString(
                sectionMarkup,
                'text/html'
              );

          const cardTemplate =
            sectionDocument.querySelector(
              'template[data-saf-wishlist-current-card]'
            );

          if (
            !(
              cardTemplate instanceof
              HTMLTemplateElement
            )
          ) {
            throw new Error(
              'Wishlist product card template not found'
            );
          }

          const html =
            cardTemplate.innerHTML.trim();

          if (!html) {
            throw new Error(
              'Wishlist product card template is empty'
            );
          }

          nativeCardHtmlCache.set(
            productUrl,
            html
          );

          return html;
        } catch (error) {
          console.error(
            'SAF wishlist native card render error:',
            productUrl,
            error
          );

          return null;
        }
      };


    /* =========================================================
       FALLBACK CARD

       Se per qualche motivo Section Rendering fallisce,
       la wishlist continua a funzionare con la vecchia card.
       ========================================================= */

    const createFallbackWishlistItem = (
      product
    ) => {
      const article =
        document.createElement(
          'article'
        );

      article.className =
        'saf-wishlist-page__card saf-wishlist-page__card--fallback';

      article.dataset.productId =
        product.id || '';

      const productUrl =
        core.getProductUrl(product);


      /* MEDIA */

      const media =
        document.createElement('div');

      media.className =
        'saf-wishlist-page__media';


      const imageLink =
        document.createElement('a');

      imageLink.className =
        'saf-wishlist-page__image-link';

      imageLink.href =
        productUrl;


      if (product.image?.url) {
        const image =
          document.createElement('img');

        image.className =
          'saf-wishlist-page__image';

        image.src =
          product.image.url;

        image.alt =
          product.image.altText ||
          product.title ||
          '';

        image.loading = 'lazy';

        image.width = 600;
        image.height = 720;

        imageLink.appendChild(
          image
        );
      } else {
        const placeholder =
          document.createElement('div');

        placeholder.className =
          'saf-wishlist-page__image-placeholder';

        placeholder.textContent =
          'NO IMAGE';

        imageLink.appendChild(
          placeholder
        );
      }

      media.appendChild(
        imageLink
      );

      media.appendChild(
        createRemoveButton(product)
      );


      /* META */

      const meta =
        document.createElement('div');

      meta.className =
        'saf-wishlist-page__meta';


      /* VENDOR */

      if (product.vendor) {
        const vendor =
          document.createElement('div');

        vendor.className =
          'saf-wishlist-page__vendor';

        vendor.textContent =
          product.vendor;

        meta.appendChild(
          vendor
        );
      }


      /* TITLE */

      const title =
        document.createElement('h2');

      title.className =
        'saf-wishlist-page__product-title';


      const titleLink =
        document.createElement('a');

      titleLink.href =
        productUrl;

      titleLink.textContent =
        product.title || '';

      title.appendChild(
        titleLink
      );

      meta.appendChild(
        title
      );


      /* PRICE */

      const priceRow =
        document.createElement('div');

      priceRow.className =
        'saf-wishlist-page__price-row';


      const price =
        document.createElement('span');

      price.className =
        'saf-wishlist-page__price';

      price.textContent =
        core.formatMoney(
          product.price,
          locale
        );

      priceRow.appendChild(
        price
      );


      const comparePrice =
        core.getComparePrice(
          product
        );

      if (
        comparePrice &&
        core.moneyNumber(
          comparePrice
        ) >
        core.moneyNumber(
          product.price
        )
      ) {
        const compare =
          document.createElement(
            'span'
          );

        compare.className =
          'saf-wishlist-page__compare-price';

        compare.textContent =
          core.formatMoney(
            comparePrice,
            locale
          );

        priceRow.appendChild(
          compare
        );
      }

      meta.appendChild(
        priceRow
      );


      article.append(
        media,
        meta,
        createAddToCartButton(
          product
        )
      );

      return article;
    };


    /* =========================================================
       CREATE WISHLIST CARD
       ========================================================= */

    const createWishlistItem =
      async (product) => {
        const nativeHtml =
          await getNativeWishlistCardHtml(
            product
          );

        /*
         * Se non riusciamo a prendere
         * la card Horizon usiamo il fallback.
         */

        if (!nativeHtml) {
          return createFallbackWishlistItem(
            product
          );
        }


        const article =
          document.createElement(
            'article'
          );

        article.className =
          'saf-wishlist-page__card saf-wishlist-page__card--native';

        article.dataset.productId =
          product.id || '';


        /*
         * Wrapper della vera card Horizon
         */

        const nativeCard =
          document.createElement('div');

        nativeCard.className =
          'saf-wishlist-page__native-card';

        nativeCard.innerHTML =
          nativeHtml;

        /*
        * Rimuove le classi specifiche PDP.
        * Stessa correzione usata nella
        * Recently Viewed.
        */

        nativeCard
          .querySelectorAll(
            '.pdp-vault-price'
          )
          .forEach(
            (element) => {
              element.classList.remove(
                'pdp-vault-price'
              );
            }
          );

        nativeCard
          .querySelectorAll(
            '.pdp-price'
          )
          .forEach(
            (element) => {
              element.classList.remove(
                'pdp-price'
              );
            }
          );

        nativeCard
          .querySelectorAll(
            '.pdp-price-label'
          )
          .forEach(
            (element) => {
              element.classList.remove(
                'pdp-price-label'
              );
            }
          );  
        /*
         * La gallery Horizon contiene già
         * il suo quick-add.
         *
         * Noi vogliamo mantenere invece
         * il nostro ADD TO CART sotto.
         *
         * Quindi togliamo il quick-add
         * interno alla gallery.
         */

        nativeCard
          .querySelectorAll(
            '.quick-add, quick-add-component'
          )
          .forEach(
            (element) => {
              element.remove();
            }
          );


        /*
         * CUORE
         */

        const remove =
          createRemoveButton(
            product
          );


        /*
         * Cerchiamo la gallery nativa Horizon.
         *
         * Il cuore viene inserito dentro
         * la gallery in modo da restare
         * sopra l'immagine.
         */

        const gallery =
          nativeCard.querySelector(
            '.card-gallery'
          ) ||
          nativeCard.querySelector(
            '[ref="cardGallery"]'
          );


        if (
          gallery instanceof HTMLElement
        ) {
          gallery.classList.add(
            'saf-wishlist-page__media-host'
          );

          gallery.style.position =
            'relative';

          gallery.appendChild(
            remove
          );
        } else {
          /*
           * fallback se cambia markup Horizon
           */

          article.appendChild(
            remove
          );
        }


        /*
         * CARD HORIZON
         */

        article.appendChild(
          nativeCard
        );


        /*
         * NOSTRO ADD TO CART
         */

        article.appendChild(
          createAddToCartButton(
            product
          )
        );


        return article;
      };


    /* =========================================================
       RENDER PAGE
       ========================================================= */

    const renderPage = async () => {
      const currentRenderVersion =
        ++renderVersion;

      updatePageCount();

      if (!grid) return;


      if (!products.length) {
        grid.replaceChildren();

        showStatus(
          root.dataset.emptyMessage ||
          'Your wishlist is empty.',
          true
        );

        return;
      }


      hideStatus();


      /*
       * Carichiamo tutte le card native.
       *
       * Promise.all mantiene l'ordine
       * dell'array products.
       */

      const cards =
        await Promise.all(
          products.map(
            (product) =>
              createWishlistItem(
                product
              )
          )
        );


      /*
       * Se nel frattempo è partito
       * un altro rendering, ignoriamo
       * questo risultato.
       */

      if (
        currentRenderVersion !==
        renderVersion
      ) {
        return;
      }


      const fragment =
        document.createDocumentFragment();


      cards.forEach(
        (card) => {
          if (card) {
            fragment.appendChild(
              card
            );
          }
        }
      );


      grid.replaceChildren(
        fragment
      );
    };


    /* =========================================================
       SORT
       ========================================================= */

    const applySort = async () => {
      const value =
        sort?.value ||
        'recent';


      products = [
        ...originalProducts,
      ];


      if (
        value === 'price-asc'
      ) {
        products.sort(
          (a, b) =>
            core.moneyNumber(
              a.price
            ) -
            core.moneyNumber(
              b.price
            )
        );
      }


      else if (
        value === 'price-desc'
      ) {
        products.sort(
          (a, b) =>
            core.moneyNumber(
              b.price
            ) -
            core.moneyNumber(
              a.price
            )
        );
      }


      else if (
        value === 'brand'
      ) {
        products.sort(
          (a, b) =>
            String(
              a.vendor || ''
            ).localeCompare(
              String(
                b.vendor || ''
              ),
              locale
            )
        );
      }


      await renderPage();
    };


    /* =========================================================
       LOAD WISHLIST
       ========================================================= */

    const loadPage = async (
      force = false
    ) => {
      if (loading) {
        return;
      }

      loading = true;


      showStatus(
        root.dataset.loadingMessage ||
        'Loading wishlist...'
      );


      try {
        const result =
          await core.getProducts(
            force
          );


        const authenticated =
          result.authenticated === true;


        root.classList.toggle(
          'is-guest',
          !authenticated
        );


        originalProducts = [
          ...result.products,
        ];


        products = [
          ...originalProducts,
        ];


        /*
         * Se i prodotti vengono ricaricati
         * invalidiamo anche HTML card.
         */

        if (force) {
          nativeCardHtmlCache.clear();
        }


        await applySort();
      } catch (error) {
        console.error(
          'SAF wishlist page load error:',
          error
        );


        root.classList.add(
          'is-guest'
        );


        grid?.replaceChildren();


        showStatus(
          root.dataset.errorMessage ||
          'Unable to load your wishlist.'
        );
      } finally {
        loading = false;
      }
    };


    /* =========================================================
       SORT EVENT
       ========================================================= */

    sort?.addEventListener(
      'change',
      () => {
        void applySort();
      }
    );


    /* =========================================================
       SHARE
       ========================================================= */

    share?.addEventListener(
      'click',
      async () => {
        const shareData = {
          title:
            document.title,

          text:
            root.dataset.shareText ||
            'My wishlist',

          url:
            window.location.href,
        };


        try {
          if (navigator.share) {
            await navigator.share(
              shareData
            );

            return;
          }


          if (
            navigator.clipboard
              ?.writeText
          ) {
            await navigator
              .clipboard
              .writeText(
                window.location.href
              );
          } else {
            const textarea =
              document.createElement(
                'textarea'
              );

            textarea.value =
              window.location.href;

            textarea.setAttribute(
              'readonly',
              ''
            );

            textarea.style.position =
              'fixed';

            textarea.style.opacity =
              '0';

            document.body.appendChild(
              textarea
            );

            textarea.select();

            document.execCommand(
              'copy'
            );

            textarea.remove();
          }


          const label =
            share.querySelector(
              '[data-saf-share-label]'
            );


          if (label) {
            const oldText =
              label.textContent;

            label.textContent =
              'COPIED';

            window.setTimeout(
              () => {
                label.textContent =
                  oldText;
              },
              1500
            );
          }
        } catch (error) {
          if (
            error?.name !==
            'AbortError'
          ) {
            console.error(
              'SAF wishlist share error:',
              error
            );
          }
        }
      }
    );


    /* =========================================================
       WISHLIST SYNC
       ========================================================= */

    core.onUpdate(
      () => {
        /*
         * Se l'update è stato causato
         * dal cuore di questa pagina,
         * abbiamo già aggiornato
         * products localmente.
         */

        if (
          ignoreNextPageUpdate
        ) {
          ignoreNextPageUpdate =
            false;

          return;
        }


        void loadPage(
          true
        );
      }
    );


    /* =========================================================
       INIT
       ========================================================= */

    void loadPage();
  }; 
  /* =========================================================
     BOOT
     ========================================================= */

  const boot =
    () => {
      initDrawer();

      document
        .querySelectorAll(
          '[data-saf-wishlist-page]'
        )
        .forEach(
          initPage
        );
    };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once: true,
      }
    );
  } else {
    boot();
  }
})();